import type { HttpClient } from "@/core/http/types";
import { agencyTasksPath } from "../agencyTasks";
import {
  AgencyTaskFilters,
  AgencyTasksResponse,
  AssignablePeopleResponse,
  CreateTaskRequest,
  ReorderTasksRequest,
  Task,
  TaskAssigneeOption,
  TaskDetail,
  TaskLink,
  TaskLinkOption,
  TaskLinkOptionsResponse,
  TaskLinkRef,
  TaskLinkResponse,
  TaskLinkType,
  TaskList,
  TaskNote,
  TaskNoteResponse,
  TaskNotesResponse,
  TasksResponse,
  UpdateTaskRequest,
} from "../types";

function handleError(error: unknown, defaultMessage: string): never {
  console.error(`TasksService Error: ${defaultMessage}`, error);
  if (error instanceof Error) throw error;
  throw new Error(defaultMessage);
}

export const tasksService = {
  /** The owner and tag pickers' people. The portal reads its own list,
   * which leaves team members' emails out (TD-103). */
  async getAssignablePeople(http: HttpClient, clientId: string): Promise<TaskAssigneeOption[]> {
    try {
      const url =
        http.context?.kind === "portal"
          ? "/api/client-portal/people"
          : `/api/clients/${clientId}/assignable-people`;
      const response = await http.get<AssignablePeopleResponse>(url);
      return response.people;
    } catch (error) {
      handleError(error, "Failed to fetch assignable people");
    }
  },

  /** The idea's own tasks, then a via group per related item (DD-052). */
  async getIdeaTasks(http: HttpClient, ideaId: string): Promise<TaskList> {
    try {
      return await http.get<TaskList>(`/api/ideas/${ideaId}/tasks`);
    } catch (error) {
      handleError(error, "Failed to fetch tasks");
    }
  },

  async getStandaloneTasks(http: HttpClient, clientId: string): Promise<Task[]> {
    try {
      const response = await http.get<TasksResponse>(
        `/api/clients/${clientId}/tasks?standalone=true`
      );
      return response.tasks;
    } catch (error) {
      handleError(error, "Failed to fetch tasks");
    }
  },

  /** `data.links` are linked in the same commit as the task. */
  async createClientTask(
    http: HttpClient,
    clientId: string,
    data: CreateTaskRequest
  ): Promise<Task> {
    try {
      return await http.post<Task>(`/api/clients/${clientId}/tasks`, data);
    } catch (error) {
      handleError(error, "Failed to create task");
    }
  },

  async updateTask(
    http: HttpClient,
    taskId: string,
    data: UpdateTaskRequest
  ): Promise<Task> {
    try {
      const url =
        http.context?.kind === "portal"
          ? `/api/client-portal/tasks/${taskId}`
          : `/api/tasks/${taskId}`;
      return await http.patch<Task>(url, data);
    } catch (error) {
      handleError(error, "Failed to update task");
    }
  },

  async deleteTask(http: HttpClient, taskId: string): Promise<void> {
    try {
      await http.delete(`/api/tasks/${taskId}`);
    } catch (error) {
      handleError(error, "Failed to delete task");
    }
  },

  /** Orders one item's own tasks: positions live on the item → task links. */
  async reorderTasks(http: HttpClient, request: ReorderTasksRequest): Promise<void> {
    try {
      await http.post(`/api/tasks/reorder`, request);
    } catch (error) {
      handleError(error, "Failed to reorder tasks");
    }
  },

  async getShotList(http: HttpClient, briefId: string): Promise<TaskList> {
    try {
      const url =
        http.context?.kind === "portal"
          ? `/api/client-portal/briefs/${briefId}/shot-list`
          : `/api/briefs/${briefId}/shot-list`;
      return await http.get<TaskList>(url);
    } catch (error) {
      handleError(error, "Failed to fetch shot list");
    }
  },

  async getAgencyTasks(
    http: HttpClient,
    filters: AgencyTaskFilters
  ): Promise<AgencyTasksResponse> {
    try {
      return await http.get<AgencyTasksResponse>(agencyTasksPath(filters));
    } catch (error) {
      handleError(error, "Failed to fetch agency tasks");
    }
  },

  /** One task, its direct links and the links carried through them (TD-096). */
  async getTask(http: HttpClient, taskId: string): Promise<TaskDetail> {
    try {
      return await http.get<TaskDetail>(`/api/tasks/${taskId}`);
    } catch (error) {
      handleError(error, "Failed to fetch task");
    }
  },

  /** A duplicate answers with the existing link, so it never fails on one. */
  async addTaskLink(http: HttpClient, taskId: string, ref: TaskLinkRef): Promise<TaskLink> {
    try {
      const response = await http.post<TaskLinkResponse>(`/api/tasks/${taskId}/links`, ref);
      return response.link;
    } catch (error) {
      handleError(error, "Failed to link task");
    }
  },

  /** `linkId` is `TaskLink.id`, the task → item row. */
  async removeTaskLink(http: HttpClient, taskId: string, linkId: string): Promise<void> {
    try {
      await http.delete(`/api/tasks/${taskId}/links/${linkId}`);
    } catch (error) {
      handleError(error, "Failed to unlink task");
    }
  },

  /** The link picker's search: one type of the client's items, at most 20. */
  async getTaskLinkOptions(
    http: HttpClient,
    clientId: string,
    type: TaskLinkType,
    q: string
  ): Promise<TaskLinkOption[]> {
    try {
      const response = await http.get<TaskLinkOptionsResponse>(
        `/api/clients/${clientId}/task-link-options`,
        { params: { type, q } }
      );
      return response.options;
    } catch (error) {
      handleError(error, "Failed to fetch link options");
    }
  },

  async getTaskNotes(http: HttpClient, taskId: string): Promise<TaskNote[]> {
    try {
      const response = await http.get<TaskNotesResponse>(`/api/tasks/${taskId}/notes`);
      return response.notes;
    } catch (error) {
      handleError(error, "Failed to fetch task notes");
    }
  },

  /** Only the body goes: the server takes the author from the session (TD-095). */
  async addTaskNote(http: HttpClient, taskId: string, body: string): Promise<TaskNote> {
    try {
      const response = await http.post<TaskNoteResponse>(`/api/tasks/${taskId}/notes`, { body });
      return response.note;
    } catch (error) {
      handleError(error, "Failed to add task note");
    }
  },
};
